import { formatPercent } from '@/shared/utils/format-percent.util';

/**
 * Linha label/valor usada nos cards de informações do detalhes da cotação
 * (layout do Figma). Compartilhada com o detalhes da proposta para manter
 * as duas telas idênticas.
 */
export function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex flex-col items-start gap-1 py-2 sm:flex-row sm:items-start sm:justify-between sm:gap-2'>
      <p className='shrink-0 font-montserrat text-sm font-bold text-alba-black-500'>
        {label}
      </p>
      <p className='min-w-0 break-words text-left font-montserrat text-sm font-normal text-alba-gray-600 sm:flex-1 sm:text-right sm:text-xs'>
        {value}
      </p>
    </div>
  );
}

type CommercializationAgent = {
  name?: string;
  commissionRate?: number;
  shareRate?: number;
};

/**
 * Seção "Comercialização" do detalhes da cotação (layout do Figma):
 * card com o percentual de corretagem seguido de um card por corretor e
 * co-corretores com participação maior que zero.
 */
export function CommercializationSection({
  agents,
}: {
  agents: CommercializationAgent[];
}) {
  const displayedAgents = (agents ?? []).filter(
    (agent, index) => index === 0 || (agent?.shareRate || 0) > 0
  );

  if (displayedAgents.length === 0) return null;

  return (
    <div className='flex flex-col gap-6'>
      <p className='font-montserrat text-base font-normal text-alba-black-500'>
        Comercialização
      </p>

      <div className='flex flex-col gap-4 w-full'>
        <div className='border border-alba-gray-400 rounded-xl p-4 flex flex-col gap-2 w-full overflow-hidden'>
          <p className='font-montserrat text-sm font-bold text-alba-black-500'>
            Percentual de Corretagem do seguro
          </p>
          <div className='w-full h-px bg-alba-gray-200' />
          <div className='flex items-center justify-between w-full'>
            <p className='font-montserrat text-sm text-alba-gray-600 sm:text-xs'>
              Percentual
            </p>
            <p className='font-montserrat text-sm text-alba-gray-600 sm:text-base'>
              {formatPercent(displayedAgents[0]?.commissionRate || 0)}
            </p>
          </div>
        </div>

        {displayedAgents.map((agent, index) => (
          <div
            key={`${agent?.name || 'agent'}-${index}`}
            className='border border-alba-gray-400 rounded-xl p-4 flex flex-col gap-2 w-full overflow-hidden'
          >
            <p className='font-montserrat text-sm font-bold text-alba-black-500'>
              {index === 0 ? 'Corretor' : `Co-corretor ${index}`}
            </p>
            <div className='w-full h-px bg-alba-gray-200' />
            <div className='flex items-center justify-between w-full'>
              <p className='font-montserrat text-sm text-alba-gray-600 sm:text-xs'>
                {agent?.name || '—'}
              </p>
              <p className='font-montserrat text-sm text-alba-gray-600 sm:text-base'>
                {formatPercent(agent?.shareRate || 0)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
